import type { FormikErrors } from 'formik'
import { formatCredits } from '@/lib/billing/utils/credits'
import { formatCentsAsUsd } from '@/lib/billing/utils/format'
import { MICRO_USD_PER_CREDIT } from '@/lib/billing/constants'
import type { NumericField } from './catalog'
import { englishPlanPresentation, formatPlanUnit, type PlanPresentation } from './presentation'
import { hoursWithDays } from './change-format'
import { parseAmount, validateEditor, type PlanEditorValues } from './model'

export interface SummaryRow {
  label: string
  value: string
}

export function buildCompactSummary(values: PlanEditorValues, presentation: PlanPresentation = englishPlanPresentation, credits: typeof formatCredits = formatCredits): SummaryRow[] {
  const { fields: F, t } = presentation
  const errors = validateEditor(values)
  const value = (field: NumericField, zero?: string) => summaryValue(values, errors, field, presentation, credits, zero)
  return [
    { label: t('monthlyPrice'), value: value(F.price, t('free')) },
    { label: t('startingCredits'), value: value(F.initial, t('noGrant')) },
    { label: t('monthlyCredits'), value: value(F.recurring, t('noGrant')) },
    { label: t('voice'), value: voiceAccess(values, value, presentation) },
    { label: t('campaigns'), value: values.campaigns_enabled ? t('campaignsEnabled', { value: value(F.campaigns, '0') }) : t('disabled') },
    { label: t('shopifyInsights'), value: values.shopify_insights_trial_duration_hours === null ? t('included') : t('trial', { value: value(F.insights) }) },
    { label: t('users'), value: value(F.users, t('none')) },
    { label: t('knowledgeStorage'), value: value(F.size, t('none')) },
    { label: t('voiceAgents'), value: voiceAgents(values, errors, value, presentation) },
    { label: t('connectedStores'), value: value(F.stores, t('none')) },
    { label: t('crmConnections'), value: value(F.crm, t('none')) },
  ]
}

function summaryValue(values: PlanEditorValues, errors: FormikErrors<PlanEditorValues>, field: NumericField, presentation: PlanPresentation, credits: typeof formatCredits, zero?: string): string {
  if (errors[field.key]) return presentation.t('needsAttention')
  const raw = values[field.key]
  if (raw === null) return field.empty ?? presentation.t('notSet')
  const amount = parseAmount(raw, field)
  if (amount === null) return presentation.t('needsAttention')
  if (amount === 0 && (zero || field.zero)) return zero || field.zero!
  if (field.scale === MICRO_USD_PER_CREDIT) return credits(amount)
  if (field.scale === 100) return formatCentsAsUsd(amount)
  if (field.unit === 'hours') return hoursWithDays(amount, presentation)
  return field.unit ? formatPlanUnit(field.unit, amount, presentation) : String(amount)
}

function voiceAccess(values: PlanEditorValues, value: (field: NumericField) => string, presentation: PlanPresentation): string {
  const { fields: F, t } = presentation
  if (values.voice_included) return t('included')
  if (values.voice_trial_enabled) return t('trial', { value: value(F.voiceDuration) })
  return t('notOffered')
}

// With voice access or a trial, a zero agent limit still permits the first agent.
function voiceAgents(values: PlanEditorValues, errors: FormikErrors<PlanEditorValues>, value: (field: NumericField) => string, presentation: PlanPresentation): string {
  const { fields: F, t } = presentation
  const offered = values.voice_included || values.voice_trial_enabled
  if (offered && !errors.voice_agents_limit && parseAmount(values.voice_agents_limit, F.agents) === 0) return t('agentToStart')
  return value(F.agents)
}
